import { useState, type ReactElement, type ReactNode } from 'react';
import type { ManagementApiKey } from '@kirin/api-client/contracts/management-api-keys';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  formatRelativeTime,
  formatDateTime,
  formatZonedDate,
} from '@/lib/date-utils';
import { cn } from '@/lib/utils';
import {
  describeScopes,
  expiryUrgency,
  managementKeyStatus,
  type ExpiryUrgency,
} from '@/lib/management-key-utils';
import { useDisplayTimeZone } from '@/hooks/useDisplayTimeZone';

interface ManagementApiKeysListProps {
  keys: ManagementApiKey[];
  isLoading: boolean;
  isError: boolean;
  /** False for support, who reads the list but cannot revoke. */
  canManage: boolean;
  onRevoke: (keyId: string) => void;
  isRevoking: boolean;
}

/**
 * A cell whose visible text is a summary and whose hover carries the detail.
 *
 * `focusable` is opt-in rather than the default: a tooltip trigger has to be
 * focusable for a keyboard or screen-reader user to reach it, but four extra
 * tab stops per row would make the table tedious to walk. So only the Access
 * cell takes one — its tooltip is the *only* place the scope list exists, while
 * the truncated name is still in the DOM in full and the dates keep their day
 * on screen.
 */
function HoverDetail({
  children,
  className,
  detail,
  focusable = false,
}: {
  children: ReactNode;
  /** Layout for the trigger itself — it is the element the cell lays out. */
  className?: string;
  detail: ReactNode;
  focusable?: boolean;
}): ReactElement {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          // `|| undefined` so a trigger with neither styling nor a focus ring
          // renders no `class` attribute at all, rather than an empty one.
          className={
            cn(
              focusable &&
                'focus-visible:ring-ring rounded-sm outline-none focus-visible:ring-2',
              className
            ) || undefined
          }
          tabIndex={focusable ? 0 : undefined}
        >
          {children}
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs break-words">{detail}</TooltipContent>
    </Tooltip>
  );
}

/**
 * The "Expires" value: how long until the key lapses — "in 2 days", "in 6
 * months" — or "Never".
 *
 * Relative rather than absolute because the question this column answers is
 * "how much time do I have", and a timestamp makes the reader do the
 * arithmetic. It also means the urgency is stated in words on every row, so the
 * red/orange colouring is reinforcement rather than the sole carrier
 * (WCAG 1.4.1).
 *
 * A key already past its expiry reads "3 days ago", which is both truthful and
 * useful; the Status column says "Expired" alongside it.
 */
function formatExpiry(expiresAt: Date | null): string {
  return expiresAt ? formatRelativeTime(expiresAt) : 'Never';
}

/**
 * Colour for the "Expires" value: red inside 14 days, orange inside 30, plain
 * beyond that — so a key about to lapse stands out while scanning the table,
 * without painting every row. `text-destructive` and
 * `text-orange-600 dark:text-orange-400` are the pairs this app already uses
 * for these two levels of emphasis.
 *
 * The heavier weight is on `imminent` only, so weight is a third cue that
 * separates the two tiers rather than something they share.
 */
const EXPIRY_URGENCY_CLASS: Readonly<Record<ExpiryUrgency, string>> = {
  imminent: 'text-destructive font-medium',
  approaching: 'text-orange-600 dark:text-orange-400',
  none: '',
};

/**
 * Table of an organization's management API keys (KIR-3748).
 *
 * Revoke is offered only on rows that are still `Active` — revoking an expired
 * or already-revoked key is a no-op the backend would reject anyway — and is
 * disabled outright for support.
 */
export function ManagementApiKeysList({
  keys,
  isLoading,
  isError,
  canManage,
  onRevoke,
  isRevoking,
}: ManagementApiKeysListProps): ReactElement {
  const timeZone = useDisplayTimeZone();
  const [pendingRevokeId, setPendingRevokeId] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="text-sm text-muted-foreground">
        Loading management API keys…
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-sm text-destructive">
        Failed to load management API keys.
      </div>
    );
  }

  if (keys.length === 0) {
    return (
      <div className="text-sm text-muted-foreground">
        No management API keys yet.
      </div>
    );
  }

  const pendingKey = keys.find(key => key.id === pendingRevokeId) ?? null;

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Prefix</TableHead>
            <TableHead>Access</TableHead>
            <TableHead>Created by</TableHead>
            <TableHead>Created</TableHead>
            <TableHead>Expires</TableHead>
            <TableHead>Last used</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {keys.map(key => {
            const status = managementKeyStatus(key);
            const urgency = expiryUrgency(key.expiresAt);
            return (
              <TableRow key={key.id}>
                <TableCell>
                  {key.name === null ? (
                    '—'
                  ) : (
                    // The width cap lives on the span, not the cell: an auto
                    // table layout ignores `max-width` on a `<td>`, so capping
                    // the cell would leave a long name stretching the table —
                    // the very thing this fixes. `truncate` keeps the full name
                    // in the DOM (and so with screen readers and Playwright's
                    // text matching); the tooltip is for sighted readers.
                    <HoverDetail
                      className="block max-w-[20ch] truncate"
                      detail={key.name}
                    >
                      {key.name}
                    </HoverDetail>
                  )}
                </TableCell>
                <TableCell className="font-mono text-xs">
                  {key.keyPrefix}…
                </TableCell>
                <TableCell>
                  {/*
                    The summary is what fits; the full list is what answers "why
                    did this key get a 403?". Hover carries the detail rather
                    than widening the column for the rare long case. A legacy
                    (null) or empty grant has no list to show, so it gets plain
                    text rather than an empty tooltip.
                  */}
                  {key.scopes === null || key.scopes.length === 0 ? (
                    describeScopes(key.scopes)
                  ) : (
                    <HoverDetail
                      focusable
                      detail={
                        <ul className="space-y-0.5">
                          {key.scopes.map(scope => (
                            <li className="font-mono text-xs" key={scope}>
                              {scope}
                            </li>
                          ))}
                        </ul>
                      }
                    >
                      {describeScopes(key.scopes)}
                    </HoverDetail>
                  )}
                </TableCell>
                <TableCell>
                  {key.createdByName ?? key.createdByEmail ?? '—'}
                </TableCell>
                <TableCell>
                  {/* Date only, full instant on hover (KIR-4377 review): the
                      minute a key was created is rarely what the reader came
                      for, and printing it widened the table too much. */}
                  <HoverDetail detail={formatDateTime(key.createdAt, timeZone)}>
                    {formatZonedDate(key.createdAt, timeZone)}
                  </HoverDetail>
                </TableCell>
                <TableCell className={EXPIRY_URGENCY_CLASS[urgency]}>
                  {/*
                    The cell shows the time left ("in 30 days"), so keep the
                    exact expiry reachable on hover — supplementary detail
                    only, since the cell's text already carries the answer and
                    the urgency. The hover is the full instant, not a UTC date:
                    the dialog sets the end of the picked UTC day, but the API
                    takes any instant, so a date alone could hide a mid-day
                    expiry.
                    Uses the same tooltip as the other three columns rather than
                    a native `title`: one table should not mix two hover
                    mechanisms that look and time differently.
                  */}
                  {key.expiresAt === null ? (
                    formatExpiry(null)
                  ) : (
                    <HoverDetail
                      detail={formatDateTime(key.expiresAt, timeZone)}
                    >
                      {formatExpiry(key.expiresAt)}
                    </HoverDetail>
                  )}
                </TableCell>
                <TableCell>
                  {/* "Never", not an em dash — the column is a promise. */}
                  {key.lastUsedAt === null ? (
                    'Never'
                  ) : (
                    <HoverDetail
                      detail={formatDateTime(key.lastUsedAt, timeZone)}
                    >
                      {formatZonedDate(key.lastUsedAt, timeZone)}
                    </HoverDetail>
                  )}
                </TableCell>
                <TableCell>
                  <Badge
                    variant={
                      status === 'Active'
                        ? 'secondary'
                        : status === 'Expired'
                          ? 'outline'
                          : 'destructive'
                    }
                  >
                    {status}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  {status === 'Active' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setPendingRevokeId(key.id)}
                      disabled={!canManage || isRevoking}
                      aria-label={`Revoke ${key.name ?? key.keyPrefix}`}
                    >
                      Revoke
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <ConfirmationDialog
        isOpen={pendingKey !== null}
        onClose={() => setPendingRevokeId(null)}
        onConfirm={() => {
          if (!pendingKey) return;
          onRevoke(pendingKey.id);
          setPendingRevokeId(null);
        }}
        title="Revoke this API key?"
        description={`"${pendingKey?.name ?? pendingKey?.keyPrefix ?? ''}" stops working immediately, and any integration using it will start failing. This cannot be undone.`}
        confirmLabel="Revoke"
        isLoading={isRevoking}
      />
    </>
  );
}
